import React from "react";
import {
  Alert,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import * as Sharing from "expo-sharing";
import { Feather } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";

import { type FeatherIconName } from "@/schemas/icons";

interface NoUsersScreenProps {
  clearFilters: () => void;
}

// TODO: Refactor NoEvents and NoUsers into a shared component.

function NoEvents({ clearFilters }: NoUsersScreenProps): React.JSX.Element {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const cardWidth = (width - 60) / 2;

  const handleSendInvites = async (): Promise<void> => {
    const inviteUrl = "getkaiapp.com";
    const message = `Join our ad-free community! ${inviteUrl}`;

    if (await Sharing.isAvailableAsync()) {
      try {
        await Sharing.shareAsync(inviteUrl);
      } catch (error) {
        await Clipboard.setStringAsync(message);
        Alert.alert("Invite link copied to clipboard!");
      }
    } else {
      await Clipboard.setStringAsync(message);
      Alert.alert("Invite link copied to clipboard!");
    }
  };

  const handleRefresh = async (): Promise<void> => {
    clearFilters();
  };

  const handleEditProfile = async (): Promise<void> => {
    router.push("/(main)/account/home");
  };

  const handleMediaUpload = (): void => {
    router.push("/(main)/media/home");
  };

  const renderCard = (
    icon: FeatherIconName,
    title: string,
    description: string,
    buttonText: string,
    buttonStyle: object,
    onPress: () => void,
  ): React.JSX.Element => (
    <TouchableOpacity
      style={[styles.card, { width: cardWidth }]}
      onPress={onPress}
    >
      <View style={styles.cardHeader}>
        <Feather name={icon} size={24} color="#4a4a4a" />
        <Text style={styles.cardTitle} numberOfLines={1}>
          {title}
        </Text>
      </View>
      <Text style={styles.cardDescription}>{description}</Text>
      <View style={[styles.button, buttonStyle]}>
        <Text style={styles.buttonText}>{buttonText}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      <LinearGradient colors={["#f0f0f0", "#ffffff"]} style={styles.gradient}>
        <Text style={styles.emoji}>🧋</Text>
        <Text style={styles.title}>No Events Found</Text>
        <Text style={styles.subtitle}>
          Be part of building an ad-free, gay, social-based community. Come back
          soon! We are adding new events every week.
        </Text>

        <View style={styles.cardContainer}>
          {renderCard(
            "users",
            "Friends",
            "Grow the community with people you know.",
            "Invite Friends",
            styles.blueButton,
            handleSendInvites,
          )}
          {renderCard(
            "user",
            "Profile",
            "Add more details to your profile",
            "Complete Profile",
            styles.purpleButton,
            handleEditProfile,
          )}
          {renderCard(
            "edit",
            "Image",
            "Upload a new image of yourself",
            "Upload Image",
            styles.pinkButton,
            handleMediaUpload,
          )}
          {renderCard(
            "refresh-cw",
            "Explore",
            "Our community grows every day. Check back again.",
            "Refresh",
            styles.orangeButton,
            handleRefresh,
          )}
        </View>
      </LinearGradient>
    </ScrollView>
  );
}

export default NoEvents;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f0f0f0",
  },
  emoji: {
    fontSize: 120,
  },
  contentContainer: {
    flexGrow: 1,
  },
  gradient: {
    flex: 1,
    padding: 20,
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
    color: "#333",
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 30,
    color: "#666",
  },
  cardContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    height: 180, // Fixed height for all cards
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
    }),
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 10,
    color: "#333",
    flex: 1,
  },
  cardDescription: {
    fontSize: 14,
    marginBottom: 15,
    color: "#666",
    flex: 1,
  },
  button: {
    borderRadius: 5,
    paddingVertical: 10,
    alignItems: "center",
  },
  buttonText: {
    color: "#fff",
    fontWeight: "bold",
  },
  blueButton: {
    backgroundColor: "#4285F4",
  },
  orangeButton: {
    backgroundColor: "#FF7F50",
  },
  purpleButton: {
    backgroundColor: "#8A2BE2",
  },
  pinkButton: {
    backgroundColor: "#FF69B4",
  },
});
